import { Redirect, useLocalSearchParams } from 'expo-router';
import useRDCOptions from 'features/rdc/hooks/useRDCOptions';
import { RDCAmountParams, RDCSearchParams } from 'features/rdc/RDCSearchParams';
import { AmountAndAccountSelector } from 'features/rdc/screens/Amount/Amount';
import { useFetchAccountDetails } from 'features/rdc/screens/Amount/hooks/useFetchAccountDetails';
import { useUpdateSelectedAccount } from 'features/rdc/screens/Amount/hooks/useUpdateSelectedAccount';
import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

// Screen that gives users the option of selecting an account, and amount for their RDC Check deposit.
export default function AmountScreen() {
  //Query Hook that fetch's available RDC Accounts for which the user can select between to deposit their check.
  const {
    isLoading: isFetchAccountsLoading,
    response,
    error: errorFetchAccounts,
    reload,
  } = useFetchAccountDetails();

  //Mutation Hook that updates the current Session ID based on the selected account.
  const {
    loading: updateSelectedAccountLoading,
    updateAccount,
    error: errorUpdateSelectedAccount,
    updatedSessionID,
  } = useUpdateSelectedAccount();
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const { session, account, depositFlowId, mode } = useLocalSearchParams<
    RDCSearchParams & RDCAmountParams
  >();
  const [sessionID, setSessionID] = useState(session);
  const error = errorFetchAccounts ?? errorUpdateSelectedAccount;
  const { rdcAvailable, rdcOptions, maxDeposit } = useRDCOptions('cache-only');

  useEffect(() => {
    if (updatedSessionID) {
      setSessionID(updatedSessionID);
    }
  }, [updatedSessionID]);

  if (!rdcAvailable || !rdcOptions || !maxDeposit) {
    return <Redirect href="./" />;
  }

  return (
    <View style={styles.screenContainer}>
      {response ? (
        <AmountAndAccountSelector
          accounts={response}
          accountSelected={updateAccount}
          sessionID={sessionID}
          preSelectedAccount={account}
          isLoadingInBackground={updateSelectedAccountLoading}
          maxDeposit={maxDeposit}
          depositFlowId={depositFlowId}
          mode={mode}
        />
      ) : (
        <ScreenTransitionOverlay
          isLoading={isFetchAccountsLoading}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            error ? (
              <DefaultPageDownErrorMessage
                error={error}
                title={defaultTitle}
                message={defaultMessage}
                tryAgainAction={reload}
              />
            ) : null
          }
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  screenContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});
